import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Tag } from 'lucide-react'
import type { Banner } from '@/types'
import { StoreImage } from '@/store/components/StoreImage'
import { cn } from '@/lib/utils'
import { homeEase } from './motion'
import {
  BANNER_AUTO_SLIDE_MS,
  BannerLinkWrap,
  bannerHref,
} from './bannerShared'

interface HomeBannersProps {
  banners: Banner[]
}

export function HomeBanners({ banners }: HomeBannersProps) {
  const reduceMotion = useReducedMotion()
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const activeBanner = banners[activeIndex]
  const hasMultiple = banners.length > 1

  const goTo = (index: number) => {
    setActiveIndex((index + banners.length) % banners.length)
  }

  const goPrev = () => goTo(activeIndex - 1)
  const goNext = () => goTo(activeIndex + 1)

  useEffect(() => {
    setActiveIndex(0)
  }, [banners])

  useEffect(() => {
    if (!hasMultiple || paused) return
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % banners.length)
    }, BANNER_AUTO_SLIDE_MS)
    return () => window.clearInterval(timer)
  }, [banners.length, hasMultiple, paused])

  useEffect(() => {
    for (const banner of banners) {
      const href = banner.imageUrl?.trim()
      if (!href) continue
      const img = new Image()
      img.src = href
    }
  }, [banners])

  if (banners.length === 0) return null

  return (
    <section
      className="relative overflow-hidden bg-[#FAF8F4]"
      aria-label="Promotions"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F4] via-[#FAF8F4] to-gold-100/25" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-500/25 to-transparent" />
      </div>

      <div className="store-container relative pb-5 pt-0 md:pb-8">
        <div className="mb-4 flex items-center justify-between gap-3 md:mb-5">
          <p className="type-eyebrow inline-flex items-center gap-1.5 text-[11px] md:text-xs">
            <Tag className="h-4 w-4 text-gold-600" aria-hidden />
            Offers
          </p>
          {hasMultiple && (
            <p className="text-xs font-medium tabular-nums text-zinc-500">
              {activeIndex + 1} / {banners.length}
            </p>
          )}
        </div>

        <AnimatePresence mode="wait">
          {(activeBanner.title || activeBanner.subtitle) && (
            <motion.div
              key={`${activeBanner.id}-heading`}
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35, ease: homeEase }}
              className="mb-4 md:mb-5"
            >
              {activeBanner.title ? (
                <h2 className="type-section-title text-2xl md:text-3xl lg:text-4xl">
                  {activeBanner.title}
                </h2>
              ) : null}
              {activeBanner.subtitle ? (
                <p className="mt-2 max-w-3xl type-body-muted text-xs md:text-sm">
                  {activeBanner.subtitle}
                </p>
              ) : null}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="gold-gradient-ring store-banner-frame overflow-hidden p-[2.5px] shadow-lg shadow-gold-500/15">
          <div className="store-banner-stage relative bg-luxury-950 ring-1 ring-inset ring-white/10">
            <div className="store-banner-media bg-luxury-900/40">
              {banners.map((banner, index) => {
                const isActive = index === activeIndex
                const href = bannerHref(banner.link)
                const label = banner.title
                  ? `Open banner: ${banner.title}`
                  : 'Open banner link'

                return (
                  <motion.div
                    key={banner.id}
                    initial={false}
                    animate={
                      reduceMotion
                        ? { opacity: isActive ? 1 : 0 }
                        : { opacity: isActive ? 1 : 0, scale: isActive ? 1 : 1.04 }
                    }
                    transition={{ duration: 0.5, ease: homeEase }}
                    className="absolute inset-0"
                    style={{
                      pointerEvents: isActive ? 'auto' : 'none',
                      zIndex: isActive ? 1 : 0,
                    }}
                    aria-hidden={!isActive}
                  >
                    <BannerLinkWrap
                      href={href}
                      label={label}
                      className="h-full w-full"
                    >
                      <StoreImage
                        src={banner.imageUrl}
                        alt={banner.title}
                        priority={index === 0}
                        objectFit="cover"
                        spinnerSize="lg"
                        containerClassName={cn(
                          'h-full w-full bg-luxury-900/50',
                          href &&
                            'transition-transform duration-700 group-hover:scale-[1.03]'
                        )}
                        className="h-full w-full object-cover"
                      />
                    </BannerLinkWrap>
                  </motion.div>
                )
              })}

              {hasMultiple && !paused && !reduceMotion && (
                <motion.div
                  key={`progress-${activeIndex}`}
                  className="absolute bottom-0 left-0 z-20 h-0.5 bg-gold-400/90"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{
                    duration: BANNER_AUTO_SLIDE_MS / 1000,
                    ease: 'linear',
                  }}
                  aria-hidden
                />
              )}
            </div>

            {hasMultiple && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous banner"
                  className={cn(
                    'absolute left-3 top-1/2 z-20 hidden -translate-y-1/2 sm:flex',
                    'h-9 w-9 items-center justify-center rounded-full',
                    'border border-white/25 bg-black/45 text-white backdrop-blur-md',
                    'transition-all hover:bg-black/65 hover:border-gold-400/40 focus-ring'
                  )}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next banner"
                  className={cn(
                    'absolute right-3 top-1/2 z-20 hidden -translate-y-1/2 sm:flex',
                    'h-9 w-9 items-center justify-center rounded-full',
                    'border border-white/25 bg-black/45 text-white backdrop-blur-md',
                    'transition-all hover:bg-black/65 hover:border-gold-400/40 focus-ring'
                  )}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>

                <div
                  className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 sm:bottom-4"
                  role="tablist"
                  aria-label="Banner slides"
                >
                  {banners.map((banner, index) => (
                    <button
                      key={banner.id}
                      type="button"
                      role="tab"
                      aria-selected={index === activeIndex}
                      aria-label={`Show banner ${index + 1}`}
                      onClick={() => setActiveIndex(index)}
                      className={cn(
                        'rounded-full transition-all duration-300 focus-ring',
                        index === activeIndex
                          ? 'h-2 w-8 bg-gold-400 shadow-sm shadow-gold-500/40'
                          : 'h-2 w-2 bg-white/45 hover:bg-white/75'
                      )}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
